/*LIBRARY*/
import { useRef, useState, useCallback } from "react";
import { connect } from "react-redux";

/*COMPONENT*/
import FLOAT_MENU from "../common_trivial/FLOAT_MENU";
import EDIT_FIELD from "../editor_table/EDIT_FIELD";
import Modal from "../common_modal/Modal";

/*REDUX*/
import { resizeField, deleteField } from "../../App/actions/layerActions";

/*PICTURE*/
import menu_icon from "../../Assets/svg/menu_icon.svg";

/*DATA*/
import { data_types } from "../../App/validation/data_types";
import dict from "../../Data/dict.json";

const TableHeader = ({
  visibleFields,
  totalWidth,
  onResize,
  requestSort,
  sortConfig,
  fields, // Full array for the menu actions
  layer_id,
  auth,
  resizeField,
  deleteField,
  parent_uuid = "",
}) => {
  const [hoveredField, setHoveredField] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [selectedFieldData, setSelectedFieldData] = useState({});

  const [modalEditField, setModalEditField] = useState(false);
  const [modalDeleteField, setModalDeleteField] = useState(false);
  const [modalPushField, setModalPushField] = useState(false);

  const resizeRef = useRef({});
  const rafRef = useRef(null);

  const language = localStorage?.language || auth.language;

  const togglePushField = useCallback(() => {
    setModalPushField((prev) => !prev);
  }, []);

  const handleMouseDown = (e, key, initialWidth) => {
    e.stopPropagation();
    resizeRef.current = {
      startX: e.clientX,
      startWidth: initialWidth,
      key,
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const { startX, startWidth, key } = resizeRef.current;
    const newWidth = Math.max(startWidth + (e.clientX - startX), 70);
    resizeRef.current.lastWidth = newWidth;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      // By calling onResize actively, we let React handle the smooth re-render natively
      if (onResize) onResize(key, newWidth);
    });
  };

  const handleMouseUp = () => {
    const { key, lastWidth } = resizeRef.current;
    if (key && lastWidth) {
      if (layer_id) {
        resizeField(layer_id, key, Number(lastWidth)); // Save to API
      }
    }
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const isMenuOpen = Boolean(anchorEl);

  const handleDeleteField = () => {
    const body = {
      geo_layer_id: layer_id,
      field_key: selectedFieldData?.key,
    };
    deleteField(body);
    setModalDeleteField(false);
  };

  const handleMenuAction = (actionStr) => {
    const fieldData = fields.find((f) => f.key === activeField);
    setSelectedFieldData(fieldData || {});

    if (actionStr === "edit") setModalEditField(true);
    if (actionStr === "delete") setModalDeleteField(true);
    if (actionStr === "sortAsc") requestSort(activeField, "ascending");
    if (actionStr === "sortDesc") requestSort(activeField, "descending");

    handleMenuClose();
  };

  return (
    <>
      <div
        role="row"
        className="table_header_row"
        style={{ position: "relative", width: totalWidth, height: "35px" }}
      >
        {/* Number Column */}
        <div
          role="columnheader"
          className="background_grey_light table_header_no_col"
          onClick={() => requestSort && requestSort(null)}
          title="Click to reset original order"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 70,
            margin: 0,
          }}
        >
          No
        </div>

        {/* Dynamic Data Columns */}
        {visibleFields.map((field) => {
          const type_current = data_types(language).find(
            (type_element) => type_element.value === field.type,
          );
          const icon_type = type_current?.icon;
          const isHovered = hoveredField === field.key;

          return (
            <div
              role="columnheader"
              key={field.key}
              onMouseEnter={() => setHoveredField(field.key)}
              onMouseLeave={() => setHoveredField(null)}
              className="background_grey_light table_header_data_col"
              style={{
                position: "absolute",
                left: field.left,
                top: 0,
                bottom: 0,
                width: field.width,
                cursor: requestSort ? "pointer" : "default",
                margin: 0,
              }}
              title={field.name}
            >
              <div className="table_header_content_wrapper">
                <div
                  className="table_header_text_wrapper"
                  style={{
                    paddingRight: isHovered ? "24px" : "0px",
                  }}
                >
                  {icon_type && (
                    <img
                      src={icon_type}
                      alt={field.type}
                      className="table_header_icon"
                    />
                  )}
                  <p
                    className="table_header_text"
                    title={field.name || field.alias || field.key}
                  >
                    {field.name || field.alias || field.key}
                  </p>

                  {sortConfig?.key === field.key && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="table_header_sort_icon"
                    >
                      {sortConfig.direction === "ascending" ? (
                        <path d="M12 19V5M5 12l7-7 7 7" />
                      ) : (
                        <path d="M12 5v14M19 12l-7 7-7-7" />
                      )}
                    </svg>
                  )}
                </div>

                {isHovered && (
                  <div
                    className="background_grey table_header_menu_btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnchorEl(e.currentTarget);
                      setActiveField(field.key);
                    }}
                  >
                    <img
                      src={menu_icon}
                      alt="Menu"
                      className="table_header_menu_icon"
                    />
                  </div>
                )}
              </div>

              {/* Resize Handle */}
              <div
                className="table_header_resize_handle"
                onMouseDown={(e) => handleMouseDown(e, field.key, field.width)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          );
        })}

        {/* Add Column Button */}
        <div
          role="columnheader"
          className="background_grey_light table_header_add_col"
          title="Tambah kolom"
          style={{
            position: "absolute",
            left: totalWidth - 50,
            top: 0,
            bottom: 0,
            width: 50,
            margin: 0,
          }}
        >
          <button onClick={togglePushField} className="table_header_add_btn">
            +
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <FLOAT_MENU
          id="table-header-menu"
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
        >
          <button
            className="button hover_primary margin_bottom"
            onClick={() => handleMenuAction("edit")}
          >
            Edit
          </button>
          <br />
          <button
            className="button hover_danger margin_bottom"
            onClick={() => handleMenuAction("delete")}
          >
            Delete
          </button>
          <br />
          <button
            className="button hover_primary margin_bottom"
            onClick={() => handleMenuAction("sortAsc")}
          >
            Sort A → Z
          </button>
          <br />
          <button
            className="button hover_primary"
            onClick={() => handleMenuAction("sortDesc")}
          >
            Sort Z → A
          </button>
        </FLOAT_MENU>
      )}

      {modalPushField && (
        <Modal
          modalSize="medium"
          id="modal_push_field"
          isOpen={modalPushField}
          onClose={togglePushField}
          title="Tambah Kolom"
        >
          <div className="box-body" id="box-body">
            <EDIT_FIELD
              togglePushField={togglePushField}
              parent_uuid={parent_uuid}
            />
          </div>
        </Modal>
      )}

      {modalEditField && (
        <Modal
          modalSize="medium"
          id="modal-edit-field"
          isOpen={modalEditField}
          onClose={() => setModalEditField(false)}
          title="Edit Kolom"
        >
          <div className="box-body" id="box-body">
            <EDIT_FIELD
              headers={fields}
              field={selectedFieldData}
              toggleEditField={() => setModalEditField(false)}
              parent_uuid={parent_uuid}
            />
          </div>
        </Modal>
      )}

      {modalDeleteField && (
        <Modal
          modalSize="small"
          id="modal-delete-field"
          isOpen={modalDeleteField}
          onClose={() => setModalDeleteField(false)}
        >
          <div className="box-body" id="box-body">
            <main>
              <section className="text_medium text_center">
                {dict?.["Are you sure to delete?"]?.[language] ||
                  "Are you sure to delete?"}
              </section>
              <p className="text_big margin_bottom text_center">
                {selectedFieldData?.name}
              </p>
              <div style={{ display: "flex", justifyContent: "end", gap: "10px" }}>
                <button
                  className="button background_gray"
                  onClick={() => setModalDeleteField(false)}
                >
                  {dict?.["Cancel"]?.[language]}
                </button>
                <button
                  className="button background_red"
                  onClick={handleDeleteField}
                >
                  {dict?.["Delete"]?.[language]}
                </button>
              </div>
            </main>
          </div>
        </Modal>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  layer_id: state.layer.layer_id,
  auth: state.auth,
  properties: state.properties,
});

export default connect(mapStateToProps, { resizeField, deleteField })(
  TableHeader,
);
